import type { PlaceValueGroup, PlaceValueAvailable, PlaceValueResponse } from '../place-value-api';
import type { FactorIcon } from './vsj-mocks';
import {
  IconCrossroad, IconHouse, IconStore, IconPulse,
} from './vsj-icons';

// ── Constants ─────────────────────────────────────────────────────────────────

const API_BASE = '/api/v1/public/projects';
const VSJ_SLUG = 'valor-san-juan';

export const BACKEND_TYPE_DISPLAY: Record<string, string> = {
  house:     'una casa',
  apartment: 'un departamento',
  land:      'un terreno',
};

const PROPERTY_TYPE_PLURAL: Record<string, string> = {
  casa:         'casas',
  departamento: 'departamentos',
  terreno:      'lotes',
  house:        'casas',
  apartment:    'departamentos',
  land:         'lotes',
};

export const FACTOR_ICONS: Record<FactorIcon, (p: { size?: number }) => JSX.Element> = {
  connected:   IconCrossroad,
  residential: IconHouse,
  services:    IconStore,
  activity:    IconPulse,
};

// ── Data types ─────────────────────────────────────────────────────────────────

export interface VsjResultData {
  currency:         string;
  pricePerM2Low:    number | null;
  pricePerM2High:   number | null;
  totalLow:         number | null;
  totalHigh:        number | null;
  observationCount: number;
  radiusM:          number;
  confidenceLevel:  1 | 2 | 3;
  confidenceLabel:  string;
}

export interface ResultFactor {
  icon:  FactorIcon;
  label: string;
}

// ── API response types ─────────────────────────────────────────────────────────

export interface ValuationResultSession {
  propertyType: string;
  areaBuiltM2:  number | null;
  areaLandM2:   number | null;
  streetLabel:  string | null;
  municipality: string | null;
}

export interface ValuationResultDto {
  session:          ValuationResultSession;
  streetTypeLabel:  string;
  roadClass:        string | null;
  placeValue:       PlaceValueResponse;
}

export interface ValuationReportDto {
  reportToken: string;
  createdAt:   string;
  snapshot:    ValuationResultDto & { createdAt: string };
}

// ── Helpers ────────────────────────────────────────────────────────────────────

export function fmtPrice(n: number | null): string {
  if (n === null) return '—';
  return Math.round(n).toLocaleString('es-AR');
}

function confidenceLevelNum(level: string): 1 | 2 | 3 {
  if (level === 'high')   return 3;
  if (level === 'medium') return 2;
  return 1;
}

export function toVsjResult(
  group:     PlaceValueGroup,
  surfaceM2: number,
  radiusM:   number,
): VsjResultData {
  const ppm2 = group.pricePerM2Range;
  const pricePerM2Low  = ppm2?.p25 ?? null;
  const pricePerM2High = ppm2?.p75 ?? null;

  let totalLow:  number | null = null;
  let totalHigh: number | null = null;

  if (pricePerM2Low !== null && pricePerM2High !== null) {
    totalLow  = Math.round(pricePerM2Low  * surfaceM2);
    totalHigh = Math.round(pricePerM2High * surfaceM2);
  } else if (group.priceRange.p25 !== null && group.priceRange.p75 !== null) {
    totalLow  = Math.round(group.priceRange.p25);
    totalHigh = Math.round(group.priceRange.p75);
  }

  return {
    currency:         group.currency,
    pricePerM2Low,
    pricePerM2High,
    totalLow,
    totalHigh,
    observationCount: group.sampleCount,
    radiusM,
    confidenceLevel:  confidenceLevelNum(group.confidenceLevel),
    confidenceLabel:  group.confidenceLabel,
  };
}

/**
 * Build context factors for the result view.
 * `propertyType` accepts both Spanish (casa/departamento/terreno) and English (house/apartment/land).
 */
export function buildResultFactors(
  streetTypeLabel: string | null,
  result:          VsjResultData,
  propertyType:    string,
): ResultFactor[] {
  const factors: ResultFactor[] = [];

  if (streetTypeLabel) {
    factors.push({ icon: 'connected', label: streetTypeLabel });
  }

  const typePlural = PROPERTY_TYPE_PLURAL[propertyType] ?? propertyType;
  factors.push({
    icon:  'activity',
    label: `${result.observationCount} referencias de ${typePlural} a menos de ${result.radiusM} m`,
  });

  if (result.totalLow !== null && result.totalHigh !== null && result.totalLow > 0) {
    const ratio = result.totalHigh / result.totalLow;
    if (ratio < 1.4) {
      factors.push({ icon: 'residential', label: 'Precios acotados en el entorno' });
    } else if (ratio > 2.0) {
      factors.push({ icon: 'residential', label: 'Precios variados en el entorno' });
    }
  }

  return factors;
}

// ── B9 — Get result from session ───────────────────────────────────────────────

export async function fetchValuationResult(sessionToken: string): Promise<ValuationResultDto> {
  const res = await fetch(`${API_BASE}/${VSJ_SLUG}/valuations/${sessionToken}/result`);
  if (!res.ok) throw new Error(`fetch-result: HTTP ${res.status}`);
  return res.json() as Promise<ValuationResultDto>;
}

// ── B12 — Create report snapshot ───────────────────────────────────────────────

export async function createValuationReport(
  sessionToken: string,
): Promise<{ reportToken: string }> {
  const res = await fetch(`${API_BASE}/${VSJ_SLUG}/valuations/${sessionToken}/report`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`create-report: HTTP ${res.status}`);
  return res.json() as Promise<{ reportToken: string }>;
}

// ── B13 — Get report from snapshot ─────────────────────────────────────────────

export async function fetchValuationReport(reportToken: string): Promise<ValuationReportDto> {
  const res = await fetch(`${API_BASE}/${VSJ_SLUG}/reports/${reportToken}`);
  if (!res.ok) throw new Error(`fetch-report: HTTP ${res.status}`);
  return res.json() as Promise<ValuationReportDto>;
}

export type { PlaceValueAvailable };
